import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as ResolvedIcon,
  Warning as PendingIcon,
  Error as EscalatedIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import axios from 'axios';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const AdminGrievanceManagement = () => {
  const { getAuthHeaders } = useAuth();
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    action_taken: '',
    status: 'Pending',
    closure_date: ''
  });

  // Fetch all grievances
  const fetchGrievances = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/existing/grievance`,
        { headers: getAuthHeaders() }
      );
      setGrievances(response.data);
    } catch (error) {
      console.error('Error fetching grievances:', error);
      toast.error('Failed to fetch grievances');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrievances();
  }, []);

  // Handle view grievance details
  const handleViewGrievance = (grievance) => {
    setSelectedGrievance(grievance);
    setEditMode(false);
    setFormData({
      action_taken: grievance.admin_section?.action_taken || '',
      status: grievance.admin_section?.status || 'Pending',
      closure_date: grievance.admin_section?.closure_date ? 
        new Date(grievance.admin_section.closure_date).toISOString().split('T')[0] : ''
    });
    setDialogOpen(true);
  };

  // Handle edit grievance
  const handleEditGrievance = (grievance) => {
    setSelectedGrievance(grievance);
    setEditMode(true);
    setFormData({
      action_taken: grievance.admin_section?.action_taken || '',
      status: grievance.admin_section?.status || 'Pending',
      closure_date: grievance.admin_section?.closure_date ? 
        new Date(grievance.admin_section.closure_date).toISOString().split('T')[0] : ''
    });
    setDialogOpen(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.action_taken.trim()) {
        toast.error('Please describe the action taken');
        return;
      }

      const adminSectionData = {
        admin_section: {
          date_received: selectedGrievance.admin_section?.date_received || new Date(), // Keep original date_received
          received_by: selectedGrievance.admin_section?.received_by || selectedGrievance.full_name, // Keep original received_by
          action_taken: formData.action_taken,
          status: formData.status,
          closure_date: formData.closure_date ? new Date(formData.closure_date) : null
        },
        grievanceId: selectedGrievance.grievanceId // Include grievanceId for soft delete
      };

      await axios.put(
        `${API_URL}/existing/grievance/admin/${selectedGrievance._id}`,
        adminSectionData,
        { headers: getAuthHeaders() }
      );

      toast.success('Grievance updated successfully');
      setDialogOpen(false);
      fetchGrievances(); // Refresh the list
    } catch (error) {
      console.error('Error updating grievance:', error);
      toast.error(error.response?.data?.error || 'Failed to update grievance');
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Resolved':
        return <ResolvedIcon color="success" />;
      case 'Pending':
        return <PendingIcon color="warning" />;
      case 'Escalated':
        return <EscalatedIcon color="error" />;
      default:
        return <PendingIcon color="warning" />;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved': return 'success';
      case 'Pending': return 'warning';
      case 'Escalated': return 'error';
      default: return 'default';
    }
  };

  // Format date using utility function
  const formatDateLocal = (dateString) => {
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ mb: 4, mt: 2 }}>
        <Typography sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: 1, fontSize: '30px', mb: 1 }}>
          Grievance Management
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ fontStyle: 'italic', fontWeight: 400, fontSize: '20px' }}>
          Manage and track employee grievances
        </Typography>
      </Box>

      {grievances.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <WarningIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '20px' }}>
                No grievances found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                No grievances have been submitted yet
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Grievance ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Nature</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Date of Incident</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grievances.map((grievance) => (
                <TableRow key={grievance._id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {grievance.grievanceId || 'N/A'}
                    </Typography>
                  </TableCell>
                                                       <TableCell>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                        {grievance.anonymous ? 'Anonymous' : grievance.full_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {grievance.anonymous ? 'Anonymous Complaint' : grievance.department || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {grievance.nature_of_grievance}
                      {grievance.grievance_other_detail && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {grievance.grievance_other_detail}
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateLocal(grievance.date_of_incident)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateLocal(grievance.date_of_submission)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(grievance.admin_section?.status)}
                      label={grievance.admin_section?.status || 'Pending'}
                      color={getStatusColor(grievance.admin_section?.status)}
                      size="small"
                      sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewGrievance(grievance)}
                          color="primary"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Status">
                        <IconButton
                          size="small"
                          onClick={() => handleEditGrievance(grievance)}
                          color="secondary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Grievance Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Update Grievance Status' : 'Grievance Details'}
        </DialogTitle>
        <DialogContent>
          {selectedGrievance && (
            <Box sx={{ mt: 2 }}>
              {/* Employee Information */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', fontSize: '20px' }}>
                    Employee Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Submission Type:</strong> {selectedGrievance.anonymous ? 'Anonymous' : 'Non-Anonymous'}
                  </Typography>
                </Grid>
                
                {!selectedGrievance.anonymous && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Full Name:</strong> {selectedGrievance.full_name || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Employee ID:</strong> {selectedGrievance.employee_id || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Department:</strong> {selectedGrievance.department || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Designation:</strong> {selectedGrievance.designation || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Contact Number:</strong> {selectedGrievance.contact_number || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Email:</strong> {selectedGrievance.email || 'N/A'}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Grievance Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Nature of Grievance:</strong> {selectedGrievance.nature_of_grievance || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedGrievance.grievance_other_detail && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Other Details:</strong> {selectedGrievance.grievance_other_detail}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date of Incident:</strong> {formatDateLocal(selectedGrievance.date_of_incident)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Reporting Date:</strong> {formatDateLocal(selectedGrievance.reporting_date)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location of Incident:</strong> {selectedGrievance.location_of_incident || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Individuals or External Vendor Involved:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedGrievance.individuals_involved || 'No individuals or external vendors involved'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Detailed Description of Grievance:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedGrievance.grievance_description || 'No description provided'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Steps Already Taken (if any):</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedGrievance.steps_taken || 'No steps taken yet'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Preferred Resolution:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedGrievance.preferred_resolution || 'No preferred resolution provided'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date of Submission:</strong> {formatDateLocal(selectedGrievance.date_of_submission)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Action Taken History - Show in both view and edit modes */}
              {selectedGrievance.actionTakenHistory && selectedGrievance.actionTakenHistory.length > 0 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '18px' }}>
                      Action Taken History
                    </Typography>
                    <Box sx={{ 
                      maxHeight: '400px', 
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'grey.50'
                    }}>
                      {selectedGrievance.actionTakenHistory.map((action, index) => (
                        <Box key={index} sx={{ mb: 3 }}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: action.deletedAt ? 'grey.50' : 'info.50', 
                            borderRadius: 2, 
                            border: '1px solid', 
                            borderColor: action.deletedAt ? 'grey.300' : 'info.300',
                            opacity: action.deletedAt ? 0.8 : 1,
                            boxShadow: action.deletedAt ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip
                                  label={action.status}
                                  color={action.status === 'Resolved' ? 'success' : action.status === 'Escalated' ? 'error' : 'warning'}
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {action.deletedAt && (
                                  <Chip
                                    label="Previous Update"
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}>
                                {formatDateTime(action.created_at)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ 
                              mt: 1,
                              lineHeight: 1.6,
                              color: 'text.primary'
                            }}>
                              {action.action_taken}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Admin Section */}
              {editMode && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '20px' }}>
                      Admin Section
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Date Received"
                          type="date"
                          value={selectedGrievance.admin_section?.date_received ? 
                            new Date(selectedGrievance.admin_section.date_received).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) : 
                            new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
                          }
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Received By"
                          value={selectedGrievance.admin_section?.received_by || (selectedGrievance.anonymous ? 'Anonymous Complaint' : selectedGrievance.full_name)}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            label="Status"
                          >
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Resolved">Resolved</MenuItem>
                            <MenuItem value="Escalated">Escalated</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Closure Date"
                          type="date"
                          value={formData.closure_date}
                          onChange={(e) => setFormData({ ...formData, closure_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          disabled={formData.status !== 'Resolved'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Action Taken"
                          value={formData.action_taken}
                          onChange={(e) => setFormData({ ...formData, action_taken: e.target.value })}
                          multiline
                          rows={4}
                          placeholder="Describe the actions taken to address this grievance..."
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Current Admin Status (View Mode) */}
              {!editMode && selectedGrievance.admin_section && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '20px' }}>
                      Current Status
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Status:</strong>
                        </Typography>
                        <Chip
                          icon={getStatusIcon(selectedGrievance.admin_section.status)}
                          label={selectedGrievance.admin_section.status}
                          color={getStatusColor(selectedGrievance.admin_section.status)}
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      {selectedGrievance.admin_section.date_received && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Date Received:</strong> {formatDateLocal(selectedGrievance.admin_section.date_received)}
                          </Typography>
                        </Grid>
                      )}
                      {selectedGrievance.admin_section.received_by && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Received By:</strong> {selectedGrievance.admin_section.received_by}
                          </Typography>
                        </Grid>
                      )}
                      
                      {/* Current Action Taken (if no history) */}
                      {(!selectedGrievance.actionTakenHistory || selectedGrievance.actionTakenHistory.length === 0) && selectedGrievance.admin_section?.action_taken && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Action Taken:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            {selectedGrievance.admin_section.action_taken}
                          </Typography>
                        </Grid>
                      )}
                      {selectedGrievance.admin_section.closure_date && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Closure Date:</strong> {formatDateLocal(selectedGrievance.admin_section.closure_date)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Close
          </Button>
          {editMode && (
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Update Status
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminGrievanceManagement; 